import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import customerAuthService from '../services/customerAuthService';

/**
 * Custom hook to handle customer session timeout
 * Tracks user activity and automatically logs out after 1 hour of inactivity
 * Similar to the main webapp login functionality
 */
const useCustomerSessionTimeout = () => {
  const navigate = useNavigate();
  const { websitePath } = useParams();
  const timeoutRef = useRef(null);
  const activityHandlersRef = useRef([]);

  useEffect(() => {
    // Only set up session timeout if customer is authenticated
    if (!customerAuthService.isAuthenticated()) {
      return;
    }

    // Check session on mount
    if (customerAuthService.isSessionExpired()) {
      console.log('[useCustomerSessionTimeout] Session expired on mount, logging out...');
      customerAuthService.logout();
      if (websitePath) {
        navigate(`/${websitePath}`, { replace: true });
      } else {
        navigate('/', { replace: true });
      }
      return;
    }

    // Update last activity timestamp
    customerAuthService.updateLastActivity();

    // Activity tracking functions
    const updateActivity = () => {
      if (customerAuthService.isAuthenticated()) {
        customerAuthService.updateLastActivity();
      }
    };

    // List of events to track for user activity
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    // Add event listeners for user activity
    const handlers = activityEvents.map(event => {
      const handler = () => updateActivity();
      window.addEventListener(event, handler, true);
      return { event, handler };
    });

    activityHandlersRef.current = handlers;

    // Check session expiration periodically (every 5 minutes)
    const checkSessionExpiration = () => {
      if (customerAuthService.isSessionExpired()) {
        console.log('[useCustomerSessionTimeout] Session expired, logging out...');
        customerAuthService.logout();
        
        // Clear all activity handlers
        handlers.forEach(({ event, handler }) => {
          window.removeEventListener(event, handler, true);
        });
        activityHandlersRef.current = [];
        
        // Navigate to home page
        if (websitePath) {
          navigate(`/${websitePath}`, { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      }
    };

    // Check every 5 minutes
    timeoutRef.current = setInterval(checkSessionExpiration, 5 * 60 * 1000);

    // Cleanup function
    return () => {
      // Clear interval
      if (timeoutRef.current) {
        clearInterval(timeoutRef.current);
        timeoutRef.current = null;
      }
      
      // Remove all event listeners
      handlers.forEach(({ event, handler }) => {
        window.removeEventListener(event, handler, true);
      });
      activityHandlersRef.current = [];
    };
  }, [navigate, websitePath]);

  // Return function to manually update activity (useful for API calls)
  return {
    updateActivity: () => {
      if (customerAuthService.isAuthenticated()) {
        customerAuthService.updateLastActivity();
      }
    }
  };
};

export default useCustomerSessionTimeout;

